import React, { useMemo, useState } from "react";
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Step, Stepper, StepLabel, Typography, TextField, MenuItem, Grid,
  Checkbox, FormControlLabel, Alert, CircularProgress, Divider, Chip,
} from "@mui/material";
import { insuranceApi, savePolicy, updatePolicy } from "services";
import { toIso2 } from "common/countryIso";

const STEPS = ["Travellers & trip", "Quote", "Order", "Payment", "Done"];
const CURRENCIES = ["GBP", "EUR", "USD"];

/**
 * Quote → Order → Process Payment wizard.
 * `employees` are the company's people (used to prefill travellers).
 * On success calls onComplete() so the parent can refresh its policy list.
 */
export default function QuoteWizard({ open, onClose, onComplete, employees = [], company }) {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [trip, setTrip] = useState({
    currency: "GBP",
    startDate: "",
    endDate: "",
    destinationCountry: "",
    residencyCountry: "GB",
  });

  const [selectedIds, setSelectedIds] = useState([]);
  const [quote, setQuote] = useState(null);
  const [order, setOrder] = useState(null);
  const [policyDocId, setPolicyDocId] = useState(null);

  const selectedEmployees = useMemo(
    () => employees.filter((e) => selectedIds.includes(e.id || e.uid || e.email)),
    [employees, selectedIds],
  );

  const reset = () => {
    setStep(0); setError(""); setQuote(null); setOrder(null);
    setSelectedIds([]); setPolicyDocId(null);
    setTrip({ currency: "GBP", startDate: "", endDate: "", destinationCountry: "", residencyCountry: "GB" });
  };

  const close = () => { reset(); onClose(); };

  const travellersPayload = () =>
    selectedEmployees.map((e) => ({
      firstName: e.firstName || e.name?.split(" ")[0] || "",
      lastName: e.lastName || e.name?.split(" ").slice(1).join(" ") || "",
      dob: e.dob || e.dateOfBirth || "",
      email: e.email || "",
    }));

  /* Step 1 → Quote */
  const handleGetQuote = async () => {
    setError(""); setLoading(true);
    try {
      const q = await insuranceApi.getQuote({
        startDate: trip.startDate,
        endDate: trip.endDate,
        currency: trip.currency,
        hostCountry: toIso2(trip.residencyCountry),
        hostCountryState: "",
        travellers: travellersPayload().map((t, i) => ({ dob: t.dob, isMain: i === 0 })),
      });
      setQuote(q);
      setStep(1);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  /* Step 2 → Order (pending) */
  const handleCreateOrder = async () => {
    setError(""); setLoading(true);
    try {
      const hostCountry = toIso2(trip.residencyCountry);
      const o = await insuranceApi.createOrder({
        quoteId: quote.quoteId,
        currency: trip.currency,
        startDate: trip.startDate,
        endDate: trip.endDate,
        depositDate: new Date().toISOString().slice(0, 10),
        travellers: travellersPayload().map((t, i) => ({
          firstName: t.firstName,
          middleName: "",
          lastName: t.lastName,
          dob: t.dob,
          email: t.email,
          contactNumber: "",
          address: "",
          city: "",
          postCode: "",
          countryCode: hostCountry,
          stateCode: "",
          isMain: i === 0,
        })),
      });
      setOrder(o);
      // Persist a pending policy record straight away.
      const id = await savePolicy({
        companyName: company || null,
        orderId: o.orderNumber || o.orderId,
        policyNumber: o.policyNumber || null,
        status: o.status || "pending",
        premium: o.premium ?? quote.premium ?? null,
        currency: o.currency || quote.currency,
        travellers: travellersPayload(),
        travellerNames: selectedEmployees.map((e) => e.name || e.email).join(", "),
        trip,
      });
      setPolicyDocId(id);
      setStep(3);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  /* Step 4 → Process Payment (approve) */
  const handlePay = async () => {
    setError(""); setLoading(true);
    try {
      // In production the payment reference comes from Stripe. In sandbox we
      // pass a synthetic reference to move the order pending → approved.
      const approved = await insuranceApi.processPayment({
        orderId: order.orderNumber || order.orderId,
      });
      if (policyDocId) {
        await updatePolicy(policyDocId, {
          status: approved.status || "approved",
          policyNumber: approved.policyNumber || order.policyNumber || null,
          documents: approved.documents || [],
        });
      }
      setOrder(approved);
      setStep(4);
      onComplete?.();
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  const canQuote =
    trip.startDate && trip.endDate && trip.destinationCountry && selectedIds.length > 0;

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        New policy
        <Chip label="Powered by battleface" size="small" sx={{ ml: 1.5, bgcolor: "#ede9fe", color: "#6d28d9", fontWeight: 600 }} />
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map((s) => (<Step key={s}><StepLabel>{s}</StepLabel></Step>))}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {step === 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Trip details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <TextField select fullWidth label="Currency" size="small" value={trip.currency}
                  onChange={(e) => setTrip({ ...trip, currency: e.target.value })}>
                  {CURRENCIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth size="small" type="date" label="Start" InputLabelProps={{ shrink: true }}
                  value={trip.startDate} onChange={(e) => setTrip({ ...trip, startDate: e.target.value })} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth size="small" type="date" label="End" InputLabelProps={{ shrink: true }}
                  value={trip.endDate} onChange={(e) => setTrip({ ...trip, endDate: e.target.value })} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth size="small" label="Destination (ISO)" placeholder="e.g. ES"
                  value={trip.destinationCountry} onChange={(e) => setTrip({ ...trip, destinationCountry: e.target.value.toUpperCase() })} />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
              Employees to insure ({selectedIds.length})
            </Typography>
            <Box sx={{ maxHeight: 220, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 2, p: 1 }}>
              {employees.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>No employees found.</Typography>}
              {employees.map((e) => {
                const key = e.id || e.uid || e.email;
                return (
                  <FormControlLabel key={key}
                    control={<Checkbox checked={selectedIds.includes(key)} size="small"
                      onChange={(ev) => setSelectedIds((prev) => ev.target.checked ? [...prev, key] : prev.filter((k) => k !== key))} />}
                    label={<span>{e.name || e.email} {e.dob ? "" : <Chip label="no DOB" size="small" sx={{ ml: 1, height: 18, fontSize: 10 }} />}</span>}
                    sx={{ display: "flex", width: "100%" }} />
                );
              })}
            </Box>
          </Box>
        )}

        {step === 1 && quote && (
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              {quote.currency} {quote.premium ?? "—"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Quote for {selectedIds.length} traveller(s), {trip.startDate} → {trip.endDate}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Quote ID: <b>{quote.quoteId || "—"}</b>
            </Typography>
          </Box>
        )}

        {step === 3 && order && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>Order created and pending payment.</Alert>
            <Typography variant="body2">Order ID: <b>{order.orderId}</b></Typography>
            <Typography variant="body2">Status: <b>{order.status || "pending"}</b></Typography>
            <Typography variant="body2">Amount due: <b>{order.currency || quote.currency} {order.premium ?? quote.premium}</b></Typography>
          </Box>
        )}

        {step === 4 && order && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>Policy approved. Documents are emailed automatically by battleface.</Alert>
            <Typography variant="body2">Order ID: <b>{order.orderId}</b></Typography>
            <Typography variant="body2">Policy no.: <b>{order.policyNumber || "—"}</b></Typography>
            <Typography variant="body2">Status: <b>{order.status || "approved"}</b></Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={close} color="inherit">Close</Button>
        {step === 0 && (
          <Button variant="contained" disabled={!canQuote || loading} onClick={handleGetQuote}
            startIcon={loading && <CircularProgress size={16} color="inherit" />}>Get quote</Button>
        )}
        {step === 1 && (
          <Button variant="contained" disabled={loading} onClick={handleCreateOrder}
            startIcon={loading && <CircularProgress size={16} color="inherit" />}>Accept & create order</Button>
        )}
        {step === 3 && (
          <Button variant="contained" disabled={loading} onClick={handlePay}
            startIcon={loading && <CircularProgress size={16} color="inherit" />}>Process payment</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
